import { useState, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import { useDispatch } from "react-redux";
import { getInstance, getTenants } from "../../../../store/instanceManagement/instanceService";
import { saveInstances } from "../../../../store/slices/instancesSlice";
import { saveTenantsList } from "../../../../store/slices/tenantsSlice";
import { setInstanceCultureName, setInstanceTimezone } from "../../../../store/slices/authSlice";
import { authService } from "../../../../utils/authService";

interface SkyPointLinkConfiguration {
  DocumentLink?: string;
  IdeaLink?: string;
  PrivacyStatementLink?: string;
  [key: string]: string | undefined;
}

interface HeaderDataState {
  selectedInstanceId: string;
  selectedInstanceName: string;
  selectedTenantBusinessName: string;
  skyPointLinkConfiguration: SkyPointLinkConfiguration;
  isInstanceLoaded: boolean;
  isTenantLoaded: boolean;
}

interface UseHeaderDataReturn extends HeaderDataState {
  refreshTenantData: () => void;
  refreshInstanceData: () => void;
}

export function useHeaderData(): UseHeaderDataReturn {
  const dispatch = useDispatch();
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>("");
  const [selectedInstanceName, setSelectedInstanceName] = useState<string>("");
  const [selectedTenantBusinessName, setSelectedTenantBusinessName] = useState<string>("");
  const [skyPointLinkConfiguration, setSkyPointLinkConfiguration] =
    useState<SkyPointLinkConfiguration>({});
  const [isInstanceLoaded, setIsInstanceLoaded] = useState<boolean>(false);
  const [isTenantLoaded, setIsTenantLoaded] = useState<boolean>(false);

  const fetchTenantData = useCallback(() => {
    getTenants()
      .then((data) => {
        data.tenants.sort((a: { businessname: string }, b: { businessname: string }) =>
          a.businessname.toLowerCase().localeCompare(b.businessname.toLowerCase()),
        );

        // Check localStorage for saved tenant ID and use it if valid
        const savedTenantId = authService.getSavedTenantId();

        if (savedTenantId && data.tenants) {
          const savedTenant = data.tenants.find(
            (t: { id: string; businessname: string }) => t.id === savedTenantId,
          );
          if (savedTenant) {
            // Override API's selectedTenant with user's saved preference
            data.selectedTenant = {
              id: savedTenant.id,
              name: savedTenant.businessname,
            };
          }
        }

        dispatch(saveTenantsList(data));
        setIsTenantLoaded(true);
      })
      .catch((error) => {
        console.error("Failed to get tenants", error);
        setIsTenantLoaded(true);
      });
  }, [dispatch]);

  const fetchInstanceData = useCallback(() => {
    getInstance()
      .then((data) => {
        dispatch(setInstanceCultureName(data?.instanceCultureName ?? "en-US"));
        dispatch(setInstanceTimezone(data?.instanceDefault.timezone ?? "US/Pacific"));

        data.instances.sort((a: { displayname: string }, b: { displayname: string }) =>
          a.displayname.toLowerCase().localeCompare(b.displayname.toLowerCase()),
        );

        // Check localStorage for saved tenant and instance IDs
        const savedTenantId = authService.getSavedTenantId();
        const savedInstanceId = authService.getSavedInstanceId();
        const backendTenantId = data.tenant?.id || "";

        let finalInstanceId = data.selectedInstanceId;
        let finalInstanceName = data.selectedInstanceName;

        // If there's a saved instance ID, check if it exists in the fetched instances
        if (savedInstanceId && data.instances) {
          const savedInstance = data.instances.find(
            (inst: { id: string; displayname: string }) => inst.id === savedInstanceId,
          );
          if (savedInstance) {
            finalInstanceId = savedInstance.id;
            finalInstanceName = savedInstance.displayname;
          }
        }

        // Update data with the resolved instance before saving to Redux
        // This ensures Redux state reflects the localStorage preference
        data.selectedInstanceId = finalInstanceId;
        data.selectedInstanceName = finalInstanceName;

        dispatch(saveInstances(data));

        // Only save to localStorage if:
        // 1. No saved tenant ID exists, OR
        // 2. The saved tenant ID matches the backend's current tenant
        // This prevents overwriting user's saved selection with backend defaults
        if (!savedTenantId || savedTenantId === backendTenantId) {
          authService.saveInstanceId(finalInstanceId);
          authService.saveTenantId(backendTenantId);
        }

        // If the URL has a stale/invalid instance ID, update it to the resolved one
        // so subsequent API calls (via baseApi) use the correct instance
        const urlParams = new URLSearchParams(window.location.search);
        const urlInstanceId = urlParams.get("instanceid") || urlParams.get("instanceId");
        if (urlInstanceId && finalInstanceId && urlInstanceId !== finalInstanceId) {
          urlParams.delete("instanceid");
          urlParams.delete("instanceId");
          urlParams.set("instanceid", finalInstanceId);
          const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
          window.history.replaceState({}, "", newUrl);
        }

        flushSync(() => {
          setSelectedInstanceId(finalInstanceId);
          setSelectedInstanceName(finalInstanceName);
          setSelectedTenantBusinessName(data.tenant.businessname);
          setSkyPointLinkConfiguration(data.skyPointLinkConfiguration || {});
          setIsInstanceLoaded(true);
        });

        // Store for analytics
        const instanceData = data?.instances.filter(
          (item: { id: string; name: string }) => item.id === finalInstanceId,
        );
        const instanceName = instanceData[0]?.name;
        const tenantName = data?.tenant?.name;
        if (tenantName && instanceName) {
          localStorage.setItem("Tenant_instance", `${tenantName.toLowerCase()}_${instanceName}`);
        }
      })
      .catch((error) => {
        console.error("Failed to get instances", error);
        setIsInstanceLoaded(true);
      });
  }, [dispatch]);

  useEffect(() => {
    fetchTenantData();
    fetchInstanceData();
  }, [fetchTenantData, fetchInstanceData]);

  return {
    selectedInstanceId,
    selectedInstanceName,
    selectedTenantBusinessName,
    skyPointLinkConfiguration,
    isInstanceLoaded,
    isTenantLoaded,
    refreshTenantData: fetchTenantData,
    refreshInstanceData: fetchInstanceData,
  };
}
